/**
 * Phase 25 Plan 25-03 Task 2 — MentionPicker (GREEN).
 *
 * The picker is a CONTROLLED satellite of a parent <textarea>: parent
 * owns the textarea ref + value + onChange. The picker:
 *  1. Watches the textarea's value + caret index for an `@partial` token.
 *  2. Calls useMentionCandidates (debounced) for the partial.
 *  3. Renders a popover absolutely positioned via getCaretCoordinates.
 *  4. On selection, calls a parent-supplied onInsert with
 *     { encodedToken, anchorIndex, partialEnd } so the parent rewrites
 *     the textarea value (D-04 + D-09 contract).
 *
 * Audience scope (CMT-07 / CMT-08) is enforced server-side by Plan 25-01;
 * the `audience` prop here is a cache-key discriminator so an internal
 * cache row can never resolve an external query (T-25-03-01 mitigation).
 */
import { useState, useRef, useEffect, useCallback, useMemo, type RefObject } from 'react';
import { useMentionCandidates } from '../../hooks/useMentionCandidates';
import type { MentionCandidate } from '../../services/api/mentionCandidates';
import { encodeMention } from './mentionEncoding';
import { getCaretCoordinates } from './caretCoords';

const PICKER_LIMIT = 8;
const FETCH_DEBOUNCE_MS = 150;

export interface MentionPickerInsertion {
  /** Encoded mention token (D-04) — `@[Name](mention:user|external:N)`. */
  encodedToken: string;
  /** Inclusive char index of the `@` trigger. */
  anchorIndex: number;
  /** Exclusive char index where the partial ends (caret position when row was selected). */
  partialEnd: number;
  /** Display name of the selected candidate (for textarea-visible insertion). */
  displayName: string;
  /** Candidate kind ('user' = internal, 'external_user' = external). */
  kind: 'user' | 'external_user';
  /** Candidate id. */
  id: number;
}

export interface MentionPickerProps {
  /** The textarea this picker watches. Parent passes the ref. */
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  /** Current textarea value (controlled). */
  value: string;
  /** Project id for the candidates query. */
  projectId: number | null;
  /** Internal vs external composer — drives audience scope on the server. */
  audience: 'internal' | 'external';
  /** Called when the user picks a row. Parent rewrites textarea value. */
  onInsert: (insertion: MentionPickerInsertion) => void;
}

interface OpenState {
  /** Inclusive char index of the `@` trigger. */
  anchorIndex: number;
  /** Substring typed after `@` so far (used as the candidates query). */
  partial: string;
  /** Highlighted row index (0-based). */
  highlight: number;
  /** Caret coords at the `@` position (used to anchor the popover). */
  coords: { top: number; left: number; height: number };
}

export function MentionPicker({
  textareaRef,
  value,
  projectId,
  audience,
  onInsert,
}: MentionPickerProps) {
  const [open, setOpen] = useState<OpenState | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce the query that drives the network fetch (keystrokes feel responsive).
  useEffect(() => {
    if (!open) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(open.partial);
    }, FETCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [open]);

  const { data: candidates = [], isFetching } = useMentionCandidates({
    projectId,
    audience,
    query: open ? debouncedQuery : '',
    enabled: open !== null,
  });

  const visibleRows = useMemo(() => candidates.slice(0, PICKER_LIMIT), [candidates]);
  const hasMore = candidates.length > PICKER_LIMIT;

  const close = useCallback(() => setOpen(null), []);

  // Watch the textarea — figure out whether we're inside an `@partial` token.
  // Trigger conditions (D-06):
  //   - The most recent `@` before the caret has no whitespace/comma between
  //     it and the caret (whitespace or comma after `@` cancels).
  //   - The `@` is at the start of the buffer OR preceded by whitespace.
  const recomputeOpen = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const caret = ta.selectionStart ?? value.length;
    const upto = value.substring(0, caret);
    const atIdx = upto.lastIndexOf('@');
    if (atIdx === -1) {
      setOpen(null);
      return;
    }
    const prevChar = atIdx === 0 ? ' ' : upto.charAt(atIdx - 1);
    if (!/\s/.test(prevChar)) {
      setOpen(null);
      return;
    }
    const partial = upto.substring(atIdx + 1);
    if (/[\s,]/.test(partial)) {
      setOpen(null);
      return;
    }
    const coords = getCaretCoordinates(ta, atIdx);
    setOpen((prev) => ({
      anchorIndex: atIdx,
      partial,
      // Preserve the user's highlight on the same `@`-trigger run; reset to 0
      // when the trigger position changes (a new `@` was started).
      highlight: prev && prev.anchorIndex === atIdx ? prev.highlight : 0,
      coords,
    }));
  }, [textareaRef, value]);

  // Recompute on value change.
  useEffect(() => {
    recomputeOpen();
  }, [recomputeOpen]);

  // Clamp the highlight if the visible row count shrinks below it.
  useEffect(() => {
    if (!open) return;
    if (visibleRows.length === 0) return;
    if (open.highlight >= visibleRows.length) {
      setOpen((prev) => (prev ? { ...prev, highlight: visibleRows.length - 1 } : prev));
    }
  }, [open, visibleRows.length]);

  // Keyboard navigation — bound to the textarea's own keydown so the
  // picker can preventDefault on arrow keys / Enter / Tab without
  // interfering with the textarea's own input handling.
  useEffect(() => {
    if (!open) return;
    const ta = textareaRef.current;
    if (!ta) return;

    function handleKey(e: KeyboardEvent) {
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen((prev) =>
          prev
            ? {
                ...prev,
                highlight: Math.min(prev.highlight + 1, Math.max(0, visibleRows.length - 1)),
              }
            : prev,
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen((prev) =>
          prev ? { ...prev, highlight: Math.max(prev.highlight - 1, 0) } : prev,
        );
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && visibleRows.length > 0) {
        e.preventDefault();
        const row = visibleRows[open.highlight] ?? visibleRows[0];
        if (row) selectRow(row);
        return;
      }
    }

    ta.addEventListener('keydown', handleKey);
    return () => ta.removeEventListener('keydown', handleKey);
    // selectRow / close are stable enough — re-bind only when open or the
    // visible candidate set changes so the closure has fresh references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visibleRows, textareaRef]);

  function selectRow(row: MentionCandidate) {
    if (!open) return;
    const displayName = displayNameOf(row);
    const kind: 'user' | 'external_user' = row.kind === 'user' ? 'user' : 'external_user';
    const encoded = encodeMention(displayName, kind, row.id);
    const partialEnd = open.anchorIndex + 1 + open.partial.length;
    onInsert({
      encodedToken: encoded,
      anchorIndex: open.anchorIndex,
      partialEnd,
      displayName,
      kind,
      id: row.id,
    });
    close();
  }

  if (!open) return null;

  return (
    <div
      role="listbox"
      aria-label="Mention candidates"
      className="absolute z-30 min-w-[260px] max-w-[320px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
      style={{ top: open.coords.top + open.coords.height + 4, left: open.coords.left }}
    >
      {visibleRows.length === 0 && !isFetching && (
        <div className="px-3 py-2 text-sm font-medium text-gray-400 italic">No matches</div>
      )}
      {visibleRows.map((row, idx) => (
        <button
          key={`${row.kind}-${row.id}`}
          type="button"
          role="option"
          aria-selected={idx === open.highlight}
          onMouseDown={(e) => {
            // mousedown to fire BEFORE the textarea blur cancels the selection.
            e.preventDefault();
            selectRow(row);
          }}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${
            idx === open.highlight ? 'bg-primary-50' : 'bg-white hover:bg-gray-50'
          }`}
        >
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold"
          >
            {initialsOf(row)}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-medium text-gray-900 truncate">{displayNameOf(row)}</span>
            <span className="block text-xs text-gray-500 truncate">{row.email}</span>
          </span>
          <span
            className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded-full ${
              row.kind === 'user' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
            }`}
          >
            {row.kind === 'user' ? 'Internal' : 'External'}
          </span>
        </button>
      ))}
      {hasMore && (
        <div className="px-3 py-2 text-xs font-medium text-gray-400 italic border-t border-gray-100">
          +{candidates.length - PICKER_LIMIT} more — keep typing
        </div>
      )}
    </div>
  );
}

function displayNameOf(row: MentionCandidate): string {
  const first = (row.firstName ?? '').trim();
  const last = (row.lastName ?? '').trim();
  const combined = `${first} ${last}`.trim();
  return combined.length > 0 ? combined : row.email;
}

function initialsOf(row: MentionCandidate): string {
  const first = (row.firstName ?? '').trim().charAt(0);
  const last = (row.lastName ?? '').trim().charAt(0);
  if (first || last) return `${first}${last}`.toUpperCase();
  return (row.email[0] ?? '?').toUpperCase();
}
